import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './KpiInfoTooltip.css'

const VIEW_MARGIN = 8
const GAP = 8

function renderTooltipParagraphs(description) {
  if (typeof description !== 'string') return description

  const paragraphs = description.split(/\n\n+/).filter(Boolean)
  if (paragraphs.length <= 1) {
    return <p className="kpiInfoTooltipPortalSectionDesc">{description}</p>
  }

  return paragraphs.map((paragraph) => (
    <p key={paragraph} className="kpiInfoTooltipPortalSectionDesc">
      {paragraph}
    </p>
  ))
}

const KpiInfoTooltip = ({
  content,
  sections,
  'aria-label': ariaLabel,
  onClick,
  onKeyDown,
  className = '',
  portaled = false,
  placement = 'auto',
  interactive = false,
}) => {
  const tooltipText = typeof content === 'string' ? content.trim() : ''
  const hasSections = Array.isArray(sections) && sections.length > 0
  const hasTooltipContent = hasSections || Boolean(tooltipText)
  const tooltipId = useId()
  const anchorRef = useRef(null)
  const tooltipRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ left: 0, top: 0, resolvedPlacement: 'bottom' })

  const updatePosition = useCallback(() => {
    const el = anchorRef.current
    if (!el) return

    const rect = el.getBoundingClientRect()
    const tip = tooltipRef.current
    const tipWidth = tip?.offsetWidth || (hasSections ? 280 : 260)
    const tipHeight = tip?.offsetHeight || (hasSections ? 150 : 64)
    const spaceAbove = rect.top - VIEW_MARGIN
    const spaceBelow = window.innerHeight - rect.bottom - VIEW_MARGIN
    const spaceRight = window.innerWidth - rect.right - VIEW_MARGIN
    const spaceLeft = rect.left - VIEW_MARGIN
    let resolvedPlacement = placement === 'auto' ? 'bottom' : placement

    if (resolvedPlacement === 'bottom' && spaceBelow < tipHeight && spaceAbove > spaceBelow) {
      resolvedPlacement = 'top'
    } else if (resolvedPlacement === 'top' && spaceAbove < tipHeight && spaceBelow > spaceAbove) {
      resolvedPlacement = 'bottom'
    } else if (resolvedPlacement === 'right' && spaceRight < tipWidth && spaceLeft > spaceRight) {
      resolvedPlacement = 'left'
    } else if (resolvedPlacement === 'left' && spaceLeft < tipWidth && spaceRight > spaceLeft) {
      resolvedPlacement = 'right'
    }

    let left
    let top
    if (resolvedPlacement === 'right') {
      left = rect.right + GAP
      top = rect.top
    } else if (resolvedPlacement === 'left') {
      left = rect.left - GAP - tipWidth
      top = rect.top
    } else if (resolvedPlacement === 'top') {
      left = rect.left
      top = rect.top - GAP - tipHeight
    } else {
      left = rect.left
      top = rect.bottom + GAP
    }

    left = Math.min(Math.max(left, VIEW_MARGIN), Math.max(VIEW_MARGIN, window.innerWidth - tipWidth - VIEW_MARGIN))
    top = Math.min(Math.max(top, VIEW_MARGIN), Math.max(VIEW_MARGIN, window.innerHeight - tipHeight - VIEW_MARGIN))

    setPosition({ left, top, resolvedPlacement })
  }, [placement, hasSections])

  const show = () => setOpen(true)
  const hide = () => setOpen(false)

  useLayoutEffect(() => {
    if (!open || !portaled) return undefined
    updatePosition()
  }, [open, portaled, updatePosition, tooltipText, hasSections])

  useEffect(() => {
    if (!open || !portaled) return undefined

    const handleReposition = () => requestAnimationFrame(updatePosition)
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)

    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [open, portaled, updatePosition])

  const icon = (
    <span
      ref={anchorRef}
      className={`liveNowCardInfoIcon dashboardSectionInfo${portaled ? ' dashboardSectionInfo--portaled' : ''} ${className}`.trim()}
      data-tooltip={!portaled && tooltipText ? tooltipText : undefined}
      aria-label={ariaLabel}
      aria-describedby={portaled && open && hasTooltipContent ? tooltipId : undefined}
      role="img"
      tabIndex={0}
      onMouseEnter={portaled ? show : undefined}
      onMouseLeave={portaled ? hide : undefined}
      onFocus={portaled ? show : undefined}
      onBlur={portaled ? hide : undefined}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      i
    </span>
  )

  const portalTooltip =
    portaled &&
    open &&
    hasTooltipContent &&
    createPortal(
      <div
        ref={tooltipRef}
        id={tooltipId}
        className={`kpiInfoTooltipPortal kpiInfoTooltipPortal--${position.resolvedPlacement}${
          hasSections ? ' kpiInfoTooltipPortal--structured' : ''
        }${interactive ? ' kpiInfoTooltipPortal--interactive' : ''}`}
        role="tooltip"
        style={{
          position: 'fixed',
          left: position.left,
          top: position.top,
          transform: 'none',
          zIndex: 6000,
        }}
      >
        {hasSections ? (
          <div className="kpiInfoTooltipPortalSections">
            {sections.map((section) => (
              <div key={section.title} className="kpiInfoTooltipPortalSection">
                <div className="kpiInfoTooltipPortalSectionTitle">{section.title}</div>
                {renderTooltipParagraphs(section.description)}
              </div>
            ))}
          </div>
        ) : (
          renderTooltipParagraphs(tooltipText)
        )}
      </div>,
      document.body
    )

  return (
    <>
      {icon}
      {portalTooltip}
    </>
  )
}

export default KpiInfoTooltip
